import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { RevealSection } from "../motion/RevealSection";
import { formatMXN } from "../../lib/whatsapp";
import { useQuoteCalculation } from "./quote-calculator/useQuoteCalculation";
import { StepIndicator } from "./quote-calculator/StepIndicator";
import { ContactStep } from "./quote-calculator/ContactStep";
import { EventStep } from "./quote-calculator/EventStep";
import { PackagesStep } from "./quote-calculator/PackagesStep";
import { ExtrasStep } from "./quote-calculator/ExtrasStep";
import { SummaryStep } from "./quote-calculator/SummaryStep";
import { STEPS } from "./quote-calculator/constants";

const STEP_INITIAL = { opacity: 0, x: 30 };
const STEP_ANIMATE = { opacity: 1, x: 0 };
const STEP_EXIT = { opacity: 0, x: -30 };
const STEP_TRANSITION = { duration: 0.35 };

export const QuoteCalculator = ({ prices, presetCategory }) => {
  const q = useQuoteCalculation(prices, presetCategory);

  return (
    <section id="cotizador" className="relative bg-black py-24 md:py-36 px-6 md:px-16">
      <RevealSection>
        <span className="text-twp-gold text-xs uppercase tracking-[0.35em]">05 — Cotizador</span>
        <h2 className="font-serif-display text-4xl md:text-6xl text-white mt-4 mb-4 max-w-2xl">
          Elige tus paquetes y calcula tu presupuesto
        </h2>
        <p className="text-white/60 text-sm mb-14 max-w-lg">
          Total estimado en tiempo real: <span className="text-twp-gold font-semibold">{formatMXN(q.total)}</span>
        </p>
      </RevealSection>

      <div className="max-w-3xl mx-auto border border-white/10 bg-twp-charcoal/40 p-6 md:p-12">
        <StepIndicator step={q.step} />

        <AnimatePresence mode="wait">
          <motion.div
            key={q.step}
            initial={STEP_INITIAL}
            animate={STEP_ANIMATE}
            exit={STEP_EXIT}
            transition={STEP_TRANSITION}
          >
            {q.step === 0 && <ContactStep form={q.form} update={q.update} />}
            {q.step === 1 && <EventStep form={q.form} update={q.update} matchedTravelCost={q.matchedTravelCost} />}
            {q.step === 2 && (
              <PackagesStep
                activeCategory={q.activeCategory}
                setActiveCategory={q.setActiveCategory}
                categoryPackages={q.categoryPackages}
                isSelected={q.isSelected}
                togglePackage={q.togglePackage}
                selectedPackages={q.selectedPackages}
                albumConfig={q.albumConfig}
              />
            )}
            {q.step === 3 && <ExtrasStep form={q.form} toggleExtra={q.toggleExtra} extras={q.extras} albumConfig={q.albumConfig} />}
            {q.step === 4 && (
              <SummaryStep
                form={q.form}
                selectedPackages={q.selectedPackages}
                submitting={q.submitting}
                result={q.result}
                total={q.total}
                extras={q.extras}
                album={q.album}
              />
            )}
          </motion.div>
        </AnimatePresence>

        {q.step < STEPS.length - 1 && (
          <div className="flex items-center justify-between mt-10">
            <button
              data-testid="quote-prev-button"
              onClick={q.prev}
              disabled={q.step === 0}
              className="flex items-center gap-2 text-white/60 text-xs uppercase tracking-wider disabled:opacity-0 hover:text-twp-gold"
            >
              <ChevronLeft size={16} /> Atrás
            </button>
            <button
              data-testid="quote-next-button"
              onClick={q.next}
              disabled={!q.canNext()}
              className="flex items-center gap-2 px-6 py-3 bg-twp-gold text-black text-xs uppercase tracking-[0.2em] font-semibold disabled:opacity-40 hover:bg-white transition-colors"
            >
              {q.step === 3 ? "Ver resumen" : "Siguiente"} <ChevronRight size={16} />
            </button>
          </div>
        )}
      </div>
    </section>
  );
};
